/**
 * HealthSignalsRow — four small cards under the main KPI strip.
 *
 *   1. Petty cash imprest — drawer balance vs imprest ceiling
 *   2. Bank match — unmatched statement rows
 *   3. TDS deposit due — current quarter's deduction sum + days to 26Q
 *   4. Approvals queue — pending count + money at stake
 *
 * Each card is a clickable anchor that deep-links to the relevant route.
 * Tone-coloured left stripe matches the prototype (`.health-card.s-*`).
 * Empty-state cards stay visible — render zero-value chrome rather than
 * hide, per the AGENTS.md §"honesty seam" pattern.
 *
 * @see prototype/home/dashboard.html `.health-signals` — V1.1 baseline
 */
import type { ReactNode } from "react";
import { Wallet, RefreshCw, ReceiptText, ShieldCheck, ArrowRight } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@flint/ui";
import { paiseToInr, paiseToInrShort } from "../../../lib/format.ts";
import { useModule } from "../../../lib/modules.ts";

export interface HealthSignalsRowProps {
  pettyCash: {
    drawerId: string | null;
    drawerName: string | null;
    balancePaise: string;
    imprestPaise: string;
  };
  bankMatch: {
    unmatchedCount: number;
    accountsWithUnmatched: number;
    primaryAccountId: string | null;
    primaryAccountName: string | null;
    oldestUnmatchedDate: string | null;
  };
  tds: {
    depositDuePaise: string;
    quarter: string;
    dueOn: string;
    daysUntilDue: number;
  };
  approvals: { pendingCount: number; totalAtStakePaise: string };
}

export function HealthSignalsRow(props: HealthSignalsRowProps) {
  // Petty cash + bank match are part of the 1.0-withheld cash module (ADR-0046):
  // hide both signals so they don't dead-end at the route guard's redirect.
  const hasCash = useModule("cash");
  return (
    <section
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
      aria-label="Health signals"
    >
      {hasCash ? <PettyCashCard {...props.pettyCash} /> : null}
      {hasCash ? <BankMatchCard {...props.bankMatch} /> : null}
      <TdsCard {...props.tds} />
      <ApprovalsCard {...props.approvals} />
    </section>
  );
}

// ── individual cards ───────────────────────────────────────────────

function PettyCashCard(props: HealthSignalsRowProps["pettyCash"]) {
  const balance = BigInt(props.balancePaise || "0");
  const imprest = BigInt(props.imprestPaise || "0");
  const hasDrawer = props.drawerId !== null;
  // Tone: amber when balance is below 30 % of imprest (reimburse soon),
  // sky otherwise. Empty state is sky-neutral.
  const tone: HealthTone =
    hasDrawer && imprest > 0n && balance * 10n < imprest * 3n ? "amber" : "sky";
  const pctOfImprest = imprest > 0n ? Math.min(Number((balance * 100n) / imprest), 100) : 0;

  return (
    <HealthCard
      tone={tone}
      icon={Wallet}
      title="Petty cash imprest"
      href={hasDrawer && props.drawerId ? `/cash/bank-accounts/${props.drawerId}` : "/cash/bank-accounts/new"}
      cta={hasDrawer ? "Open drawer" : "Set up a drawer"}
      subtitle={props.drawerName ?? "No drawer on file"}
    >
      {hasDrawer ? (
        <>
          <p className="flex items-baseline gap-1.5 text-lg font-bold leading-tight text-ink num tracking-tight">
            {paiseToInr(props.balancePaise)}
            {imprest > 0n ? (
              <span className="text-xs font-medium text-muted num">
                / {paiseToInrShort(props.imprestPaise)}
              </span>
            ) : null}
          </p>
          {imprest > 0n ? (
            <div className="h-1 mt-1 mb-0.5 bg-line rounded-full overflow-hidden">
              <div
                className={cn(
                  "h-full rounded-full transition-[width] duration-300",
                  tone === "amber" ? "bg-warn" : "bg-sky-600",
                )}
                style={{ width: `${pctOfImprest}%` }}
              />
            </div>
          ) : null}
        </>
      ) : (
        <p className="text-lg font-semibold leading-tight text-muted">—</p>
      )}
    </HealthCard>
  );
}

function BankMatchCard(props: HealthSignalsRowProps["bankMatch"]) {
  const tone: HealthTone =
    props.unmatchedCount === 0 ? "emerald" : props.unmatchedCount >= 5 ? "rose" : "amber";
  const subtitle =
    props.unmatchedCount === 0
      ? "All statement rows matched"
      : props.primaryAccountName
        ? `Bank match · ${props.primaryAccountName}`
        : "Across all bank accounts";
  return (
    <HealthCard
      tone={tone}
      icon={RefreshCw}
      title="Match transactions"
      href="/cash/reconcile"
      cta={props.unmatchedCount === 0 ? "Open workbench" : "Open workbench"}
      subtitle={subtitle}
    >
      {props.unmatchedCount === 0 ? (
        <p className="text-lg font-bold leading-tight text-ink num">0</p>
      ) : (
        <>
          <p className="flex items-baseline gap-1.5 text-lg font-bold leading-tight text-ink num">
            {props.unmatchedCount.toLocaleString("en-IN")}
            <span className="text-xs font-medium text-muted">unmatched</span>
          </p>
          {props.oldestUnmatchedDate ? (
            <p className="text-xs text-muted">
              Since {formatShortDate(props.oldestUnmatchedDate)}
              {props.accountsWithUnmatched > 1
                ? ` · across ${String(props.accountsWithUnmatched)} accounts`
                : ""}
            </p>
          ) : null}
        </>
      )}
    </HealthCard>
  );
}

function TdsCard(props: HealthSignalsRowProps["tds"]) {
  const due = BigInt(props.depositDuePaise || "0");
  // Tone: rose when within a week or past due, amber when within a month,
  // sky-neutral otherwise. Zero collected = sky.
  const tone: HealthTone =
    due === 0n
      ? "sky"
      : props.daysUntilDue <= 7
        ? "rose"
        : props.daysUntilDue <= 30
          ? "amber"
          : "sky";
  return (
    <HealthCard
      tone={tone}
      icon={ReceiptText}
      title="TDS deposit due"
      href="/compliance/tds"
      cta="Prepare challan"
      subtitle={`TDS · ${props.quarter}`}
    >
      <p className="text-lg font-bold leading-tight text-ink num">
        {paiseToInr(props.depositDuePaise)}
      </p>
      <p className="text-xs text-muted">
        Challan due {formatShortDate(props.dueOn)}
        {props.daysUntilDue >= 0
          ? ` · ${String(props.daysUntilDue)} day${props.daysUntilDue === 1 ? "" : "s"}`
          : ` · ${String(Math.abs(props.daysUntilDue))} day${Math.abs(props.daysUntilDue) === 1 ? "" : "s"} ago`}
      </p>
    </HealthCard>
  );
}

function ApprovalsCard(props: HealthSignalsRowProps["approvals"]) {
  const tone: HealthTone =
    props.pendingCount === 0 ? "emerald" : props.pendingCount >= 3 ? "amber" : "sky";
  return (
    <HealthCard
      tone={tone}
      icon={ShieldCheck}
      title="Approvals queue"
      href="/approvals"
      cta="Review approvals"
      subtitle={props.pendingCount === 0 ? "Inbox clear" : "Awaiting your OTP"}
    >
      {props.pendingCount === 0 ? (
        <p className="text-lg font-bold leading-tight text-ink num">0</p>
      ) : (
        <>
          <p className="flex items-baseline gap-1.5 text-lg font-bold leading-tight text-ink num">
            {props.pendingCount.toLocaleString("en-IN")}
            <span className="text-xs font-medium text-muted">
              item{props.pendingCount === 1 ? "" : "s"}
            </span>
          </p>
          {BigInt(props.totalAtStakePaise || "0") > 0n ? (
            <p className="text-xs text-muted">
              <span className="num">{paiseToInr(props.totalAtStakePaise)}</span> at stake
            </p>
          ) : null}
        </>
      )}
    </HealthCard>
  );
}

// ── shared card shape ──────────────────────────────────────────────

type HealthTone = "sky" | "amber" | "rose" | "emerald";

const TONE_STRIPE: Record<HealthTone, string> = {
  sky: "bg-sky-600",
  amber: "bg-warn",
  rose: "bg-bad",
  emerald: "bg-ok",
};

const TONE_FOOT: Record<HealthTone, string> = {
  sky: "text-sky-700",
  amber: "text-warn",
  rose: "text-bad",
  emerald: "text-ok",
};

interface HealthCardProps {
  tone: HealthTone;
  icon: LucideIcon;
  title: string;
  subtitle: string;
  href: string;
  cta: string;
  children: ReactNode;
}

function HealthCard({ tone, icon: Icon, title, subtitle, href, cta, children }: HealthCardProps) {
  return (
    <a
      href={href}
      className="group relative overflow-hidden bg-paper border border-line rounded-md shadow-sm transition-all hover:-translate-y-px hover:border-line-2 hover:shadow-md text-ink no-underline"
    >
      <span aria-hidden className={cn("absolute inset-y-0 left-0 w-[3px]", TONE_STRIPE[tone])} />
      <div className="px-3.5 py-3 pl-4 flex flex-col gap-1">
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted">
          <Icon size={12} aria-hidden strokeWidth={2} />
          {title}
        </div>
        <p className="text-xs text-ink-2 font-semibold truncate" title={subtitle}>
          {subtitle}
        </p>
        {children}
        <p
          className={cn(
            "mt-0.5 text-xs font-semibold inline-flex items-center gap-1",
            TONE_FOOT[tone],
          )}
        >
          {cta}
          <ArrowRight size={11} aria-hidden />
        </p>
      </div>
    </a>
  );
}

/** "2026-07-31" → "31 Jul". Compact for the small card meta line. */
function formatShortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}
